/* Progressive enhancement: all content remains visible without JavaScript. */
(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  let observer;
  let storyPanels = [];
  let storyStack;
  let header;
  let footerReveal;
  let footerObserver;
  function updateFooterMode() {
    if (!footerReveal) return;
    document.body.classList.add('sp-footer-js');
    const headerHeight = document.getElementById('header-group')?.getBoundingClientRect().height ?? 0;
    document.body.classList.toggle('sp-footer-can-pin', footerReveal.getBoundingClientRect().height <= window.innerHeight - headerHeight - 8);
  }
  function updateStories() {
    const viewport = window.innerHeight;
    const stackBounds = storyStack?.getBoundingClientRect();
    const headerBounds = header?.getBoundingClientRect();
    document.body.classList.toggle('sp-stories-active', Boolean(stackBounds && headerBounds && stackBounds.top <= headerBounds.top + 1 && stackBounds.bottom >= headerBounds.bottom));
    const horizontal = Boolean(storyStack?.hasAttribute("data-sp-story-carousel"));
    const bounds = storyPanels.map(panel => panel.getBoundingClientRect());
    storyPanels.forEach((panel, index) => {
      const rect = bounds[index];
      // Clamp the media inside its 15% overscan, including on short mobile screens.
      const progress = Math.max(0, Math.min(1, (viewport - rect.top) / (viewport + rect.height)));
      panel.style.setProperty('--sp-story-shift', (reduced.matches || horizontal) ? '0px' : `${(progress - 0.5) * Math.min(200, rect.height * 0.24)}px`);
      const video = panel.querySelector('.sp-story-video');
      if (!video) return;
      const visibleBottom = Math.min(viewport, rect.bottom, horizontal ? Infinity : (bounds[index + 1]?.top ?? Infinity));
      const visible = visibleBottom - Math.max(0, rect.top) > Math.min(160, viewport * 0.25);
      if (visible && (!horizontal || (rect.right > stackBounds.left + 1 && rect.left < stackBounds.right - 1)) && !document.hidden && !reduced.matches) {
        if (video.paused) video.play().catch(() => {});
      } else if (!video.paused) video.pause();
    });
  }
  function init(root = document) {
    if (observer) observer.disconnect();
    storyPanels = [...document.querySelectorAll('.sp-story-panel')];
    storyStack = document.querySelector('.sp-story-stack');
    header = document.querySelector('[data-sp-header]');
    if (storyStack?.hasAttribute('data-sp-story-carousel') && !storyStack.dataset.spCarouselReady) {
      const track = storyStack;
      const panels = [...track.querySelectorAll('.sp-story-panel')];
      const wrapper = track.closest('.sp-story-carousel');
      const pages = [...wrapper.querySelectorAll('[data-sp-story-page]')];
      const prev = wrapper.querySelector('[data-sp-story-prev]');
      const next = wrapper.querySelector('[data-sp-story-next]');
      const current = () => Math.max(0, Math.min(panels.length - 1, Math.round(track.scrollLeft / track.clientWidth)));
      const sync = () => {
        const index = current();
        pages.forEach((button, i) => { if (i === index) button.setAttribute('aria-current', 'true'); else button.removeAttribute('aria-current'); });
        prev.disabled = index === 0; next.disabled = index === panels.length - 1;
        updateStories();
      };
      const go = index => track.scrollTo({left: Math.max(0, Math.min(panels.length - 1, index)) * track.clientWidth, behavior: reduced.matches ? 'auto' : 'smooth'});
      pages.forEach((button, i) => button.addEventListener('click', () => go(i)));
      prev.addEventListener('click', () => go(current() - 1));
      next.addEventListener('click', () => go(current() + 1));
      track.addEventListener('keydown', event => {
        if (event.target !== track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault(); go(current() + (event.key === 'ArrowRight' ? 1 : -1));
      });
      track.addEventListener('scroll', sync, {passive: true});
      document.addEventListener('shopify:block:select', event => { const i = panels.findIndex(panel => panel.contains(event.target)); if (i >= 0) go(i); });
      track.dataset.spCarouselReady = 'true'; sync();
    }

    footerReveal = document.querySelector('[data-sp-footer-reveal]');
    if (footerReveal && !footerReveal.dataset.spFocusReveal) {
      footerReveal.dataset.spFocusReveal = 'true';
      footerReveal.addEventListener('focusin', event => {
        if (!document.body.classList.contains('sp-footer-can-pin')) return;
        const mainBottom = document.getElementById('MainContent')?.getBoundingClientRect().bottom ?? 0;
        const targetTop = event.target.getBoundingClientRect().top;
        if (mainBottom > targetTop - 12) window.scrollBy(0, Math.ceil(mainBottom - targetTop + 16));
      });
    }
    if (footerObserver) footerObserver.disconnect();
    if (footerReveal && 'ResizeObserver' in window) {
      footerObserver = new ResizeObserver(updateFooterMode);
      footerObserver.observe(footerReveal);
    }
    updateFooterMode();
    const detail = document.querySelector("section[class^='product-showcase-'], .sp-process-hydrah, .sp-process");
    if (detail && !document.getElementById('sp-product-details')) detail.id = 'sp-product-details';
    document.querySelectorAll('.sp-process-sourcepur__header, .sp-process-hydrah__header, .sp-process__header, .sp-comparatif__header, .spc-head, .sp-faq-section-head').forEach(el => el.setAttribute('data-sp-reveal', ''));
    // Legacy selectable cards become keyboard-operable without changing their logic.
    document.querySelectorAll('.qz-opt, .spc-product, .sp-install .sp-card').forEach(el => {
      if (el.dataset.spKeyboard) return;
      el.dataset.spKeyboard = 'true';
      el.tabIndex = 0;
      el.setAttribute('role', 'button');
      const sync = () => el.setAttribute('aria-pressed', String(el.classList.contains('selected') || el.classList.contains('is-on')));
      sync();
      new MutationObserver(sync).observe(el, { attributes: true, attributeFilter: ['class'] });
      el.addEventListener('keydown', event => {
        if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); el.click(); }
      });
    });
    if (!reduced.matches && 'IntersectionObserver' in window) {
      observer = new IntersectionObserver(entries => entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('sp-visible');
          observer.unobserve(entry.target);
        }
      }), { rootMargin: '0px 0px -35px 0px', threshold: 0.06 });
      document.querySelectorAll('[data-sp-reveal]').forEach((el) => {
        if (el.getBoundingClientRect().top > window.innerHeight) el.classList.add('sp-will-reveal');
        observer.observe(el);
      });
    } else document.querySelectorAll('.sp-will-reveal').forEach(el => el.classList.remove('sp-will-reveal'));
  }
  let scheduled = false;
  function onScroll() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      document.body.classList.toggle('sp-scrolled', window.scrollY > 55);
      const hero = document.querySelector('[data-sp-parallax]');
      if (hero && !reduced.matches && window.innerWidth > 700 && window.scrollY < window.innerHeight * 1.5) {
        hero.style.transform = `translate3d(0,${Math.min(window.scrollY * 0.16, 135)}px,0)`;
      } else if (hero) hero.style.transform = '';
      const mainBottom = document.getElementById('MainContent')?.getBoundingClientRect().bottom ?? Infinity;
      footerReveal?.classList.toggle('sp-footer-visible', mainBottom < window.innerHeight - 24);
      const diagnosticMedia = document.querySelector('[data-sp-diagnostic-media]');
      if (diagnosticMedia) {
        const diagnostic = diagnosticMedia.parentElement;
        const stick = !reduced.matches && window.innerWidth <= 900;
        const shift = stick ? Math.max(0, Math.round(-diagnostic.getBoundingClientRect().top)) : 0;
        diagnosticMedia.style.transform = stick ? `translate3d(0,${shift}px,0)` : '';
      }
      updateStories();
      const signature = document.querySelector('.sp-footer-wordmark');
      if (signature && !reduced.matches && !signature.classList.contains('sp-footer-arrived')) {
        const rect = signature.getBoundingClientRect();
        if (rect.top < window.innerHeight * 0.9 && rect.bottom > 0 && rect.top >= mainBottom - 20) {
          signature.classList.add('sp-footer-arrived');
        }
      }
      scheduled = false;
    });
  }
  document.addEventListener('keydown', event => {
    if (event.key === 'Escape') document.querySelectorAll('.sp-mobile-nav[open]').forEach(menu => { menu.open = false; menu.querySelector('summary').focus(); });
  });
  document.addEventListener('click', event => {
    document.querySelectorAll('.sp-mobile-nav[open]').forEach(menu => {
      if (!menu.contains(event.target) || event.target.closest('a')) menu.open = false;
    });
  });
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', () => { updateFooterMode(); onScroll(); }, { passive: true });
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) document.querySelectorAll('.sp-story-video').forEach(video => video.pause());
    else onScroll();
  });
  document.addEventListener('shopify:section:load', () => { init(); onScroll(); });
  document.addEventListener('shopify:section:unload', () => { init(); onScroll(); });
  document.addEventListener('shopify:section:select', event => event.target.querySelectorAll('[data-sp-reveal]').forEach(el => el.classList.add('sp-visible')));
  reduced.addEventListener('change', () => { init(); onScroll(); });
  document.fonts?.ready.then(updateFooterMode);
  init(); onScroll();
})();

(() => {
  let overlay = null;
  let priorOverflow = '';
  let trigger = null;
  const closeOverlay = () => {
    if (!overlay) return;
    overlay.classList.remove('sp-video-overlay');
    overlay.querySelector('.sp-video-close')?.remove();
    document.body.style.overflow = priorOverflow;
    overlay = null;
    trigger?.focus();
  };
  const fallback = (card, button) => {
    closeOverlay();
    overlay = card;
    trigger = button;
    priorOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    card.classList.add('sp-video-overlay');
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'sp-video-close';
    close.textContent = '×';
    close.setAttribute('aria-label', document.documentElement.lang.startsWith('en') ? 'Close fullscreen video' : 'Fermer la vidéo en plein écran');
    close.addEventListener('click', closeOverlay);
    card.append(close);
    close.focus();
  };
  document.addEventListener('keydown', event => {
    if (!overlay) return;
    if (event.key === 'Escape') closeOverlay();
    if (event.key === 'Tab') {
      event.preventDefault();
      overlay.querySelector('.sp-video-close')?.focus();
    }
  });
  document.addEventListener('click', event => {
    const button = event.target.closest('.sp-video-expand');
    if (!button) return;
    const card = button.closest('.sp-video-library-card,.sp-video-frame');
    const video = card?.querySelector('video');
    if (!video) return;
    event.preventDefault();
    document.querySelectorAll('.sp-video-library-card video').forEach(other => { if (other !== video) other.pause(); });
    video.controls = true;
    if (video.requestFullscreen && document.fullscreenEnabled) {
      video.requestFullscreen().catch(() => fallback(card, button));
    } else if (typeof video.webkitEnterFullscreen === 'function') {
      try { video.webkitEnterFullscreen(); } catch (_) { fallback(card, button); }
    } else fallback(card, button);
    video.play().catch(() => {});
  });
})();
